import { ReactNode, useEffect, useState } from "react";
import { Error, Loading } from "..";
import { SizeProp } from "@fortawesome/fontawesome-svg-core";

interface LoadingErrorProps<TValue> {
    value: TValue | null | undefined;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    loaded: (value: TValue) => ReactNode;
    loadingSize?: SizeProp;
    loadingDelay?: number;
}

/**
 * Displays a loading indicator or an error message, or calls loaded with the value.
 * If isError is true, an error message is displayed.
 * If isLoading is true, a delayed loading indicator is displayed.
 * If isLoading is not defined and value is null, a loading indicator is displayed.
 * If value is undefined or null, an error message is displayed.
 * Otherwise, loaded is called with the value.
 */
export const LoadingError = <TValue,>(props: LoadingErrorProps<TValue>) => {
    const [showLoading, setShowLoading] = useState(props.loadingDelay ?? 0 === 0);

    useEffect(() => {
        if (!showLoading) {
            const timer = setTimeout(() => setShowLoading(true), props.loadingDelay ?? 0);
            return () => clearTimeout(timer);
        }
    }, [props.value]);

    if (props.isError) return <Error onRetry={props.onRetry} />;

    if (props.isLoading) return showLoading ? <Loading size={props.loadingSize ?? "2x"} /> : null;

    if (props.isLoading === undefined && props.value === null) return showLoading ? <Loading size={props.loadingSize ?? "2x"} /> : null;

    if (!props.value) return <Error onRetry={props.onRetry} />;

    return props.loaded(props.value);
};
